<!-- NavBar.vue -->
<script setup lang="ts">
  import { ref, watch } from 'vue'
  import { INavbarData } from '@/types'
  import { NavButton } from '@components/index'

  import LogoIcon from '@static/logo.svg'

  const route = useRoute()
  const routePath = ref(route.path)

  const props = defineProps<INavbarData>()
  const mobile = ref(false)
  const mobileNav = ref(false)
  const windowWidth = ref(0)

  const handleClick = (route: string) => {
    if (route !== '/') {
      handleToggleNav()
    }
    props.onClick(route)
  }

  const handleToggleNav = () => {
    mobileNav.value = !mobileNav.value
  }

  const checkScreen = () => {
    windowWidth.value = window.innerWidth
    if (windowWidth.value <= 720) {
      mobile.value = true
      return
    }
    mobile.value = false
    mobileNav.value = false
  }

  onMounted(() => {
    checkScreen()
    window.addEventListener('resize', checkScreen)
  })

  onUnmounted(() => {
    window.removeEventListener('resize', checkScreen)
  })

  watch(
    () => route.path,
    () => {
      routePath.value = route.path
    }
  )
</script>

<template>
  <header>
    <nav>
      <div class="navbar-brand">
        <LogoIcon
          class="branding-logo mt-1"
          filled
          @click="() => handleClick('/')"
        />
      </div>

      <!-- Menu Web -->
      <ul
        v-show="!mobile"
        class="navigation"
      >
        <li
          v-for="(button, index) in props.buttons"
          :key="index"
          :class="button.route == routePath ? 'icon-menu-burgue-active' : ''"
        >
          <NavButton
            :icon="button.icon"
            :alt-title="button.title"
            :title="button.title"
            :on-click="() => handleClick(button.route)"
          />
        </li>
      </ul>

      <!-- Burgue -->
      <div
        v-show="mobile"
        class="icon-menu-burgue"
        :class="{ 'icon-menu-burgue-active': mobileNav }"
      >
        <NavButton
          icon="list"
          alt-title="Menu"
          title=""
          font-size="28px"
          :on-click="() => handleToggleNav()"
        />
      </div>

      <!-- Menu Mobile -->
      <transition name="mobile-nav">
        <div
          v-show="mobile"
          class="navbar-box"
          :class="mobileNav ? 'open' : ''"
        >
          <ul
            v-show="mobileNav"
            class="dropdown-nav"
          >
            <li
              v-for="(button, index) in props.buttons"
              :key="index"
              :class="button.route == routePath ? 'icon-menu-burgue-active' : ''"
            >
              <NavButton
                :icon="button.icon"
                :alt-title="button.title"
                :title="button.title"
                :on-click="() => handleClick(button.route)"
              />
            </li>
          </ul>
        </div>
      </transition>
    </nav>
  </header>
</template>

<style scoped lang="scss">
  header {
    z-index: 99;
    left: 0;
    right: 0;
    margin: auto;
    padding-top: 20px;
    padding-bottom: 10px;
    background-image: url('/static/background-header-page.png');
    background-repeat: no-repeat;
    background-size: auto;
    background-position: center top;
    background-attachment: fixed;
  }

  nav {
    // position: relative;
    display: flex;
    flex-direction: row;
    flex: 1;
    justify-content: space-between;
    padding: 0px;
    margin: auto;
    gap: 12px;
    transition: 0.5s ease all;

    ul {
      list-style: none;
      text-decoration: none;
    }

    .navbar-brand {
      margin: 0;
      padding: 0;
      height: 54px;
      display: flex;
      align-items: center;

      .branding-logo {
        width: auto;
        height: 34px;
        cursor: pointer;
      }
    }

    .navigation {
      margin: 0;
      padding: 0;
      height: 54px;
      display: flex;
      align-items: center;
      flex: 1;
      justify-content: flex-end;
      gap: 48px;
    }

    .icon-menu-burgue {
      display: flex;
      align-items: center;
      top: 0;
      right: 0;
      position: absolute;
      height: 100%;
    }

    .icon-menu-burgue-active {
      color: #39e980;
    }

    .navbar-box {
      z-index: 999;
      position: absolute;
      top: 60px;
      right: 0;
      background-color: #222529aa;
      border-bottom-left-radius: 9px;
      border: 2px solid var(--hover-border-color);
      box-shadow: 0 0 10px var(--hover-box-shadow);
      overflow: hidden;
      max-height: 0;
      opacity: 0;
      transition:
        max-height 2s ease,
        opacity 0.5s ease;
    }
    .navbar-box.open {
      max-height: 500px;
      opacity: 1;
    }

    .dropdown-nav {
      display: flex;
      flex-direction: column;
      width: 100%;
      max-width: 100px;
      height: auto;
      padding: 10px 25px;
      margin-right: 60px;
      margin-bottom: 0px;
      li {
        margin-right: 0;
      }
    }
  }

  .mobile-nav-enter-active,
  .mobile-nav-leave-active {
    transition: 10s ease all;
  }

  .mobile-nav-enter-from,
  .mobile-nav-leave-to {
    transform: translateX(300px);
  }

  .mobile-nav-enter-to,
  .mobile-nav-leave-from {
    transform: translateX(0);
  }
</style>
